import { useEffect, useMemo, useRef, useState } from 'react'
import { api, can, type Assessment, type MessageLadder } from '../lib/api'
import { TASK_VERB } from '../lib/status'
import { hashParam, setHashParam, useDrawerParam, useHashParam } from '../lib/hashParams'
import { isSendAddress } from '../lib/sendSteps'
import { ErrorBox } from '../components/DataTable'
import { Skeleton } from '../components/ui'
import { ChaseButton } from '../components/ChaseButton'
import { Dialog, MiniTrack, Pager, Status, Toggletip, clampPage, cleanSize } from '../components/kit'
import { AssessTabs } from '../components/assess/AssessTabs'
import { AssessmentTracker, type AnsweredCount } from '../components/assess/AssessmentTracker'
import { ChannelIcon } from '../components/assess/ChannelIcon'
import { ReminderLadder } from '../components/assess/ReminderLadder'
import { SendQuestionnaire } from './SendQuestionnaire'
import {
  OPEN,
  REVIEW,
  SCORED,
  channelName,
  dayMonth,
  isLate,
  ladderWord,
  latestLadder,
  questionnaireProgress,
  questionnaireStatus,
} from '../components/assess/journey'
import '../styles/home.css'
import '../components/assess/assess.css'

// Every questionnaire in flight, tracked like a parcel: one line each with the
// four-dot track of where it is, and a drawer with the full track and the
// reminders that have gone out for it.
//
// # THE FILTER IS THE COUNT
//
// Each segment's number is the number of rows it shows, worked out by the same
// rule Home uses: a questionnaire is overdue when it is with the vendor (sent,
// being answered, or returned for more) and its due date has passed. The
// segment is kept in the address, so a tile on Home can open exactly the
// questionnaires it counted.
//
// # WHY THE REMINDER COLUMN IS NOT ALWAYS FILLED
//
// Reminders are read per vendor. The list reads them for the vendors whose
// questionnaires are with them, up to a fixed number, and leaves the cell empty
// for the rest rather than saying "none" about something it did not read. The
// drawer always reads them.
//
// # THE LIST, THE DRAWER AND THE SEND (RELEASE 3.3, O1)
//
// The Progress cell is never empty: the four dots and the stage reached, or
// "Not sent" (journey.ts questionnaireProgress). A row opens the drawer at
// ?panel=<id>: pushed, so Back closes it (Back model B5, B7, B10); the old
// ?open=<id> links (Home, the programme report) are moved to it on arrival.
// The drawer has the tracker, the reminders sent and the one Send reminder
// control (components/ChaseButton.tsx), and opens the assessment. The list is
// paged at 25 (?page, ?size, replaced like the filter). Send questionnaire is
// three steps at #/assessments/send (views/SendQuestionnaire.tsx), the same
// send the vendor's screen makes. The lists beside this one are the row of
// tabs at the top (components/assess/AssessTabs.tsx).

type Show = 'all' | 'out' | 'review' | 'overdue' | 'scored'
// The filters are named with the vocabulary's words (lib/status.ts): Sent
// holds every questionnaire still with the vendor (sent, in progress or sent
// back for clarification), Submitted every one waiting for a review.
const SHOWS: { key: Show; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'out', label: 'Sent' },
  { key: 'review', label: 'Submitted' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'scored', label: 'Scored' },
]

// How many vendors' reminders the list reads, and how many at once.
const LADDER_VENDORS = 30
const LADDER_PARALLEL = 4

type Row = Assessment & { late: boolean; group: 0 | 1 | 2 | 3 }
type SortKey = 'vendor' | 'state' | 'due'

function asShow(want: string | null): Show {
  return (SHOWS.find((s) => s.key === want)?.key ?? 'all') as Show
}

function inShow(r: Row, show: Show): boolean {
  switch (show) {
    case 'out':
      return r.group === 2
    case 'review':
      return REVIEW.has(r.status)
    case 'overdue':
      return r.late
    case 'scored':
      return SCORED.has(r.status)
    default:
      return true
  }
}

// An old link's ?open=<id> (Home's journeys, the programme report) moved to
// ?panel=<id> in place, written the one way the console writes the address
// (lib/hashParams.ts) and replaced, so Back is not an extra step. A link that
// names both keeps panel.
export function movePanelLink(): void {
  const old = hashParam('open')
  if (old === null) return
  if (hashParam('panel') === null) setHashParam('panel', old, 'replace')
  setHashParam('open', null, 'replace')
}

// The rows of one page, with the page and size the address asked for made
// usable: a size the pager does not offer is 25, a page past the end is the
// last one (kit/flow.ts).
export function pageOf<T>(rows: T[], rawPage: string | null, rawSize: string | null): { rows: T[]; page: number; size: number } {
  const size = cleanSize(rawSize)
  const page = clampPage(Number(rawPage ?? '1') || 1, rows.length, size)
  return { rows: rows.slice((page - 1) * size, page * size), page, size }
}

export function Assessments() {
  // The send has its own address under this screen's route, so the menu and
  // App.tsx need nothing new for it.
  if (typeof window !== 'undefined' && isSendAddress(window.location.hash)) return <SendQuestionnaire />
  return <AssessmentList />
}

function AssessmentList() {
  // An old link's ?open= becomes ?panel= before anything reads the address.
  useState(() => {
    movePanelLink()
    return 0
  })
  const [rows, setRows] = useState<Row[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  // The filter and the page are replaced in the address, so Back leaves the
  // page rather than stepping through each click; the open questionnaire is
  // pushed, so Back closes its drawer and a link opens it (Back model B5, B7,
  // B10).
  const [showParam, setShowParam] = useHashParam('show', 'replace')
  const [pageParam, setPageParam] = useHashParam('page', 'replace')
  const [sizeParam, setSizeParam] = useHashParam('size', 'replace')
  const drawer = useDrawerParam('panel')
  const address = { show: asShow(showParam), open: drawer.value ?? '' }
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'state', dir: 1 })
  const [ladders, setLadders] = useState<Map<string, MessageLadder[]>>(new Map())

  useEffect(() => {
    let live = true
    api
      .assessments()
      .then((a) => {
        if (!live) return
        setRows(
          (a.assessments ?? []).map((x) => {
            const late = isLate(x)
            const group: Row['group'] = REVIEW.has(x.status) ? 0 : late ? 1 : OPEN.has(x.status) ? 2 : 3
            return { ...x, late, group }
          }),
        )
      })
      .catch((err) => {
        if (live) setError(err)
      })
    return () => {
      live = false
    }
  }, [])

  // Reminders for the vendors whose questionnaires are with them.
  const ladderVendors = useMemo(
    () => [...new Set((rows ?? []).filter((r) => OPEN.has(r.status)).map((r) => r.vendorId))].slice(0, LADDER_VENDORS),
    [rows],
  )
  useEffect(() => {
    if (ladderVendors.length === 0) return
    let live = true
    const queue = [...ladderVendors]
    const work = async () => {
      for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
        const vendorId = id
        try {
          const r = await api.vendorChannels(vendorId)
          if (!live) return
          setLadders((m) => new Map(m).set(vendorId, r.ladders ?? []))
        } catch {
          // Left unread: the cell stays empty and the drawer reads again.
        }
      }
    }
    void Promise.all(Array.from({ length: LADDER_PARALLEL }, work))
    return () => {
      live = false
    }
  }, [ladderVendors])

  // A new filter starts at its first page.
  const setShow = (show: Show) => {
    setPageParam(null)
    setShowParam(show === 'all' ? null : show)
  }
  const openRow = (id: string) => drawer.open(id)
  const close = () => void drawer.close()
  // After Send reminder in the drawer, the row's Reminder cell reads its
  // vendor's ladders again, so the list and the drawer say the same.
  const rereadLadder = (vendorId: string) => {
    api
      .vendorChannels(vendorId)
      .then((r) => setLadders((m) => new Map(m).set(vendorId, r.ladders ?? [])))
      .catch(() => undefined)
  }

  if (error != null) return <ErrorBox error={error} what="Assessments" />
  if (!rows)
    return (
      <div className="p1-page hm-page">
        <AssessTabs at="assessments" />
        <div className="as-head">
          <h1>Assessments</h1>
        </div>
        <Skeleton lines={6} height={18} />
      </div>
    )

  const counts: Record<Show, number> = {
    all: rows.length,
    out: rows.filter((r) => inShow(r, 'out')).length,
    review: rows.filter((r) => inShow(r, 'review')).length,
    overdue: rows.filter((r) => inShow(r, 'overdue')).length,
    scored: rows.filter((r) => inShow(r, 'scored')).length,
  }
  const shown = rows
    .filter((r) => inShow(r, address.show))
    .sort((a, b) => {
      const by =
        sort.key === 'vendor'
          ? (a.vendorName ?? '').localeCompare(b.vendorName ?? '', 'en')
          : sort.key === 'due'
            ? (a.dueOn ?? '9999').localeCompare(b.dueOn ?? '9999')
            : a.group - b.group || (a.dueOn ?? '9999').localeCompare(b.dueOn ?? '9999')
      return by * sort.dir
    })
  const paged = pageOf(shown, pageParam, sizeParam)
  const selected = rows.find((r) => r.id === address.open) ?? null

  const header = (key: SortKey, label: string) => (
    <th scope="col" aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : 1 }))}>
        {label}
      </button>
    </th>
  )

  return (
    <div className="p1-page hm-page">
      <AssessTabs at="assessments" />
      <div className="as-head">
        <div>
          <h1>Assessments</h1>
          <span className="hm-seg" role="group" aria-label="Show questionnaires">
            {SHOWS.map((s) => (
              <button key={s.key} type="button" aria-pressed={address.show === s.key} onClick={() => setShow(s.key)}>
                {s.label}
                <b>{counts[s.key]}</b>
              </button>
            ))}
          </span>
        </div>
        {/* One questionnaire to one vendor, in three steps. A bulk send is
            raised from Bulk assessments, and its approval is a decision in
            My work only. */}
        {can('questionnaire.issue') && (
          <div className="as-actions">
            <a className="button primary" href="#/assessments/send">
              {TASK_VERB.send}
            </a>
          </div>
        )}
      </div>

      {/* A bulk send waiting for a second person was announced here as well as
          on Home, in the Campaigns list and on its own page (release 3.2,
          removal item 7). It is a decision in My work now, and the Campaigns
          list says its state. */}
      <section className="panel hm-panel">
        {shown.length === 0 ? (
          <div className="hm-empty">
            <strong>{rows.length === 0 ? 'No questionnaire has been sent yet' : 'No questionnaire here'}</strong>
            {rows.length === 0 ? (
              <a className="hm-link" href="#/vendors">
                Open the register
              </a>
            ) : (
              <span className="hm-muted">Choose another filter above</span>
            )}
          </div>
        ) : (
          <div className="as-table-wrap">
            <table className="as-table">
              <caption className="vh">Questionnaires. Choose a vendor to see its tracker and reminders.</caption>
              <thead>
                <tr>
                  {header('vendor', 'Vendor')}
                  <th scope="col">Progress</th>
                  {header('state', 'Status')}
                  <th scope="col" className="as-col-due" aria-sort={sort.key === 'due' ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
                    <button type="button" onClick={() => setSort((s) => ({ key: 'due', dir: s.key === 'due' ? (s.dir === 1 ? -1 : 1) : 1 }))}>
                      Due
                    </button>
                  </th>
                  <th scope="col" className="as-col-reminder">
                    Reminders
                  </th>
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((r) => {
                  const st = questionnaireStatus(r)
                  return (
                    <tr key={r.id} onClick={() => openRow(r.id)}>
                      <td>
                        <button type="button" className="as-open" onClick={(e) => { e.stopPropagation(); openRow(r.id) }}>
                          {r.vendorName || 'Vendor'}
                        </button>
                        <span className="as-sub kit-mono">{r.reference}</span>
                      </td>
                      <td>
                        <ProgressCell status={r.status} />
                      </td>
                      <td>
                        <Status tone={st.tone}>{st.text}</Status>
                      </td>
                      <td className="as-col-due kit-mono">{r.dueOn ? dayMonth(r.dueOn) : <span className="hm-muted">None</span>}</td>
                      <td className="as-col-reminder">
                        <ReminderCell row={r} ladders={ladders.get(r.vendorId)} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {shown.length > 0 && (
          <div className="as-pager">
            <Pager
              label="Questionnaire pages"
              page={paged.page}
              size={paged.size}
              total={shown.length}
              onPage={(p) => setPageParam(p === 1 ? null : String(p))}
              onSize={(n) => {
                setPageParam(null)
                setSizeParam(n === 25 ? null : String(n))
              }}
            />
          </div>
        )}
      </section>

      <AssessmentDrawer row={selected} onClose={close} onChased={rereadLadder} />
    </div>
  )
}

// Never empty (O1): the dots and the stage, or "Not sent".
export function ProgressCell({ status }: { status: string }) {
  const p = questionnaireProgress(status)
  if (!p.sent) return <span className="as-progress hm-muted">{p.word}</span>
  return (
    <span className="as-progress">
      <MiniTrack states={p.states} label={p.label} />
      <span className="as-progress-word" aria-hidden="true">
        {p.word}
      </span>
    </span>
  )
}

function ReminderCell({ row, ladders }: { row: Row; ladders: MessageLadder[] | undefined }) {
  if (!OPEN.has(row.status) || ladders === undefined) return null
  const l = latestLadder(ladders, row.id)
  if (!l) return <span className="as-reminder">None yet</span>
  if (l.state === 'RUNNING') {
    const inFlight = [...l.rungs].sort((a, b) => b.rungNo - a.rungNo)[0]
    if (!inFlight) return <span className="as-reminder">Starting</span>
    return (
      <span className="as-reminder">
        <ChannelIcon channel={inFlight.channel} size={14} />
        {channelName(inFlight.channel)}
        {inFlight.at && <span className="kit-mono">{dayMonth(inFlight.at)}</span>}
      </span>
    )
  }
  const w = ladderWord(l.state)
  return <Status tone={w.tone}>{w.text}</Status>
}

// Whether the drawer's read starts from empty. Another questionnaire does, so
// nothing of the last shows under it, and so does any opening after the
// drawer was closed, even of the same one (closed, nothing is kept: review of
// 13 Oct). A re-read of the one open (after Send reminder) keeps what is on
// screen until its answer arrives: cleared, the tracker and the reminders
// blinked empty and back (O1 review nit, 13 Oct 2026). `last` is the
// questionnaire read for, '' while the drawer is closed.
export function drawerRead(last: string, id: string): { clear: boolean; last: string } {
  if (!id) return { clear: false, last: '' }
  return { clear: last !== id, last: id }
}

function AssessmentDrawer({ row, onClose, onChased }: { row: Row | null; onClose: () => void; onChased?: (vendorId: string) => void }) {
  const [ladders, setLadders] = useState<MessageLadder[] | null>(null)
  // Bumped after Send reminder, so the ladder is read again and says it went.
  const [reads, setReads] = useState(0)
  // Kept with the id it was counted for, so a count never shows under another
  // questionnaire for the one render before the next read starts.
  const [answered, setAnswered] = useState<{ id: string; count: AnsweredCount } | undefined>(undefined)
  const [failed, setFailed] = useState(false)
  // The last questionnaire shown, kept while the drawer slides away.
  const [shown, setShown] = useState<Row | null>(row)
  useEffect(() => {
    if (row) setShown(row)
  }, [row])

  const id = row?.id ?? ''
  const vendorId = row?.vendorId ?? ''
  // The questionnaire the drawer last read for (see drawerRead).
  const readFor = useRef('')
  useEffect(() => {
    const read = drawerRead(readFor.current, id)
    readFor.current = read.last
    if (!id) return
    let live = true
    if (read.clear) {
      setLadders(null)
      setAnswered(undefined)
      setFailed(false)
    }
    // The same count the review screen shows: a question is answered when it
    // has a value or is marked not applicable. A read that fails leaves the
    // tracker without a count rather than with a wrong one.
    api
      .assessment(id)
      .then((d) => {
        if (!live) return
        const by = new Map(d.responses.map((r) => [r.questionKey, r]))
        const done = d.questions.filter((q) => {
          const r = by.get(q.questionKey)
          return r !== undefined && (r.isNa || (r.value !== undefined && r.value !== ''))
        }).length
        setAnswered({ id, count: { done, total: d.questions.length } })
      })
      .catch(() => undefined)
    api
      .vendorChannels(vendorId)
      .then((r) => {
        if (!live) return
        setFailed(false)
        setLadders((r.ladders ?? []).filter((l) => l.assessmentId === id).sort((a, b) => b.startedAt.localeCompare(a.startedAt)))
      })
      .catch(() => {
        if (live) setFailed(true)
      })
    return () => {
      live = false
    }
  }, [id, vendorId, reads])

  const a = row ?? shown
  if (!a) return null
  const reviewing = REVIEW.has(a.status)
  // The API sends the invitation again for a questionnaire sent or in
  // progress, late or not, to a reader who may issue one.
  const remind = can('questionnaire.issue') && (a.status === 'ISSUED' || a.status === 'IN_PROGRESS')

  return (
    <Dialog
      shape="drawer"
      kind="record"
      open={row !== null}
      onClose={onClose}
      title={a.vendorName || 'Vendor'}
      primary={{ label: reviewing ? TASK_VERB.review : 'Open assessment', href: '#/assessment/' + a.id }}
      cancel={false}
      more={
        <>
          <a className="hm-link" href={'#/vendor/' + a.vendorId + '/full/messages'}>
            All messages
          </a>
          {/* The one reminder control (release 3.2, removal item 1), drawn
              here as on the vendor's next step and in My work: one component,
              one call, one guard per questionnaire (O1 of the 7 Oct plan). */}
          {remind && (
            <ChaseButton
              assessmentId={a.id}
              actionId="tprm.assessments.a01"
              onChased={() => {
                setReads((n) => n + 1)
                onChased?.(a.vendorId)
              }}
            />
          )}
        </>
      }
    >
      <p className="as-drawer-sub">
        <span className="kit-mono">{a.reference}</span>
        {a.respondentEmail && <span title={a.respondentEmail}>{a.respondentEmail}</span>}
      </p>
      <AssessmentTracker assessment={a} answered={answered?.id === a.id ? answered.count : undefined} />
      <section aria-labelledby="as-reminders">
        <div className="as-section-head">
          <h3 id="as-reminders">Reminders</h3>
          <Toggletip label="How reminders work">
            A reminder goes out as the due date nears and again once it passes. It tries each channel in turn and stops when one is
            delivered or the vendor answers.
          </Toggletip>
        </div>
        {failed ? (
          <p className="as-line muted">The reminders could not be read</p>
        ) : ladders === null ? (
          <p className="as-line muted">Loading</p>
        ) : ladders.length === 0 ? (
          <p className="as-line muted">No reminder has gone out for this questionnaire</p>
        ) : (
          ladders.slice(0, 3).map((l) => <ReminderLadder key={l.id} ladder={l} />)
        )}
        {ladders && ladders.length > 3 && (
          <p className="hm-more">
            {ladders.length - 3} earlier on the vendor's <a href={'#/vendor/' + a.vendorId + '/full/messages'}>Messages</a> tab
          </p>
        )}
      </section>
    </Dialog>
  )
}
